import React, {
  createContext,
  useCallback,
  useState,
  useMemo,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useEditor, useEditorDispatch } from 'pages/editor/editor-context';
import json2mjml from 'json2mjml';
import { addIdsToJson, buildTagId } from 'utils/mjml/util';

export function EditorPanelLeftContextProvider({ children: Children }) {
  const { fileId, componentId, teamId, tagSelected, json } = useEditor();
  const { editor, setTagSelected } = useEditorDispatch(
    teamId,
    fileId,
    componentId
  );

  const [jsonState, setJsonState] = useState(json);

  const bodyIndex = jsonState?.children.findIndex(
    (mjmlChildren) => mjmlChildren.tagName === 'mj-body'
  );

  const bodyArray = useMemo(() => {
    return jsonState?.children[bodyIndex]?.children || [];
  }, [jsonState, bodyIndex]);

  const [bodyChildren, setBodyChildren] = useState(bodyArray);
  const bodyArrayWithIds = addIdsToJson(
    jsonState?.children[bodyIndex]
  )?.children;

  const [bodyChildrenWithIds, setBodyChildrenWithIds] =
    useState(bodyArrayWithIds);

  useEffect(() => {
    if (json) {
      setJsonState(json);
    }
    if (jsonState) {
      setBodyChildren(jsonState?.children[bodyIndex]?.children);
      setBodyChildrenWithIds(
        addIdsToJson(jsonState?.children[bodyIndex]).children
      );
    }
  }, [json, jsonState, bodyIndex, setJsonState]);

  // remove the ids to the json to convert it to mjml
  const removeIds = useCallback((jsonElement) => {
    const { id, children, ...rest } = jsonElement;
    if (children) {
      return {
        ...rest,
        children: children.map(removeIds),
      };
    }
    return rest;
  }, []);

  const updateMjmlState = useCallback(
    async (jsonElement, id) => {
      const jsonElementWithoutId = removeIds(jsonElement);
      const mjml = json2mjml(jsonElementWithoutId);
      await editor.save(mjml);

      if (id === null) return;

      setTagSelected({
        id,
        from: 'panel',
      });
    },
    [removeIds, editor, setTagSelected]
  );

  const findParents = useCallback((node, selectedId, parents) => {
    if (node?.id === selectedId) {
      return true;
    }
    if (node?.children) {
      return node?.children.some((child) => {
        const found = findParents(child, selectedId, parents);
        if (found && node.id !== selectedId) {
          parents.add(node);
        }
        return found;
      });
    }
    return false;
  }, []);

  /**
   * It adds a new block to the body of the email
   * @param tag - the tag that you want to add to the body
   */
  const handleAddBlock = useCallback(
    (block) => {
      const items = block.content.blockArray;
      const newBodyChildren = [...bodyChildren];
      const newJson = { ...jsonState };
      items.forEach((item) => {
        const newItem = { ...item };
        newItem.id = buildTagId(item.tagName);
        newItem.attributes.name = block.name;
        newBodyChildren.push(newItem);
      });

      newJson.children[bodyIndex].children = newBodyChildren;

      const lastIndex = newBodyChildren.length - 1;

      setJsonState(newJson);
      setBodyChildren(newBodyChildren);
      setBodyChildrenWithIds(
        addIdsToJson(newJson.children[bodyIndex]).children
      );

      updateMjmlState(jsonState, newBodyChildren[lastIndex].id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bodyChildren, jsonState]
  );

  const sharedData = useMemo(() => {
    return {
      setBodyChildren,
      setBodyChildrenWithIds,
      bodyIndex,
      bodyChildren,
      bodyChildrenWithIds,
      jsonState,
      updateMjmlState,
      setTagSelected,
      tagSelected,
      setJsonState,
      removeIds,
      handleAddBlock,
      teamId,
      fileId,
      findParents,
    };
  }, [
    bodyIndex,
    bodyChildren,
    jsonState,
    updateMjmlState,
    setTagSelected,
    tagSelected,
    bodyChildrenWithIds,
    removeIds,
    handleAddBlock,
    teamId,
    fileId,
    findParents,
  ]);

  return (
    <EditorPanelLeftContext.Provider value={sharedData}>
      {Children}
    </EditorPanelLeftContext.Provider>
  );
}

EditorPanelLeftContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const EditorPanelLeftContext = createContext();
